import React, { useState, useCallback, useRef, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Button, TextField, Grid, Box, Typography } from "@mui/material";

// Utility functions for color conversion
const hexToRgb = hex => {
  // Handle 3-digit hex codes
  if (hex && hex.length === 4) {
    hex = "#" + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
  }

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
    : null;
};

const rgbToHex = (r, g, b) => {
  return (
    "#" +
    [r, g, b]
      .map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
};

const hsvToRgb = (h, s, v) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r, g, b;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else {
    r = c;
    g = 0;
    b = x;
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  };
};

const rgbToHsv = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  if (diff !== 0) {
    if (max === r) {
      h = ((g - b) / diff) % 6;
    } else if (max === g) {
      h = (b - r) / diff + 2;
    } else {
      h = (r - g) / diff + 4;
    }
  }
  h = Math.round(h * 60);
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : diff / max;
  const v = max;

  return { h, s, v };
};

const isValidHex = hex => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
};

// Custom Color Picker Component
const CustomColorPicker = ({ color, onChange }) => {
  const canvasRef = useRef(null);
  const sliderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSliderDragging, setIsSliderDragging] = useState(false);
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(1);
  const [value, setValue] = useState(1);

  // Initialize HSV from hex color
  useEffect(() => {
    if (color) {
      const rgb = hexToRgb(color);
      if (rgb) {
        const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
        setHue(hsv.h);
        setSaturation(hsv.s);
        setValue(hsv.v);
      }
    }
  }, [color]);

  // Draw the saturation/value canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;

    // Create gradient
    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const s = x / width;
        const v = 1 - y / height;
        const rgb = hsvToRgb(hue, s, v);

        const index = (y * width + x) * 4;
        data[index] = rgb.r;
        data[index + 1] = rgb.g;
        data[index + 2] = rgb.b;
        data[index + 3] = 255;
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }, [hue]);

  const handleCanvasInteraction = e => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const newSaturation = Math.max(0, Math.min(1, x / rect.width));
    const newValue = Math.max(0, Math.min(1, 1 - y / rect.height));

    setSaturation(newSaturation);
    setValue(newValue);

    const rgb = hsvToRgb(hue, newSaturation, newValue);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    onChange(hex);
  };

  const handleSliderInteraction = e => {
    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const x = e.clientX - rect.left;

    const newHue = Math.max(0, Math.min(360, (x / rect.width) * 360));
    setHue(newHue);

    const rgb = hsvToRgb(newHue, saturation, value);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    onChange(hex);
  };

  // Global mouse events for dragging
  useEffect(() => {
    const handleMouseMove = e => {
      if (isDragging) {
        handleCanvasInteraction(e);
      }
      if (isSliderDragging) {
        handleSliderInteraction(e);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsSliderDragging(false);
    };

    if (isDragging || isSliderDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, isSliderDragging, hue, saturation, value]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Main color area */}
      <Box sx={{ position: "relative" }}>
        <canvas
          ref={canvasRef}
          width={280}
          height={200}
          style={{
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "crosshair",
            display: "block"
          }}
          onMouseDown={e => {
            setIsDragging(true);
            handleCanvasInteraction(e);
          }}
        />
        {/* Saturation/Value indicator */}
        <Box
          sx={{
            position: "absolute",
            width: 16,
            height: 16,
            border: "2px solid white",
            borderRadius: "50%",
            pointerEvents: "none",
            transform: "translate(-8px, -8px)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
            left: `${saturation * 280}px`,
            top: `${(1 - value) * 200}px`
          }}
        />
      </Box>

      {/* Hue slider */}
      <Box sx={{ position: "relative", height: 24, width: 280 }}>
        <Box
          ref={sliderRef}
          sx={{
            width: "100%",
            height: "100%",
            borderRadius: 1,
            cursor: "pointer",
            background:
              "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)"
          }}
          onMouseDown={e => {
            setIsSliderDragging(true);
            handleSliderInteraction(e);
          }}
        />
        {/* Hue indicator */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            width: 8,
            height: "100%",
            bgcolor: "white",
            border: "1px solid #999",
            borderRadius: 0.5,
            pointerEvents: "none",
            transform: "translateX(-4px)",
            left: `${(hue / 360) * 100}%`
          }}
        />
      </Box>
    </Box>
  );
};

const ColorPickerDialog = ({
  open,
  initialColor = "#3b82f6",
  label = "Color Picker",
  subtitle = "",
  saveChanges,
  handleClose
}) => {
  const [tempColor, setTempColor] = useState(initialColor);
  const [hexInput, setHexInput] = useState(initialColor);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Reset temp color when modal opens or initialColor changes
  useEffect(() => {
    if (open) {
      setTempColor(initialColor);
      setHexInput(initialColor);
    }
  }, [open, initialColor]);

  // Get RGB values from current color
  const rgbValues = hexToRgb(tempColor);

  const handleColorChange = useCallback(
    newColor => {
      setTempColor(newColor);
      if (!isInputFocused) {
        setHexInput(newColor);
      }
    },
    [isInputFocused]
  );

  const handleHexInputChange = e => {
    const value = e.target.value;
    setHexInput(value);

    if (isValidHex(value)) {
      setTempColor(value);
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
    // Reset input to current color if invalid
    if (!isValidHex(hexInput)) {
      setHexInput(tempColor);
    }
  };

  const handleSave = () => {
    const payload = {
      hex: tempColor,
      rgb: rgbValues,
      rgbString: `rgb(${rgbValues.r}, ${rgbValues.g}, ${rgbValues.b})`
    };

    // if (onChange && rgbValues) {
    //   onChange(payload);
    // }
    if (saveChanges && rgbValues) {
      saveChanges(payload);
    }
  };

  const handleCancel = () => {
    setTempColor(initialColor);
    setHexInput(initialColor);

    if (handleClose) {
      handleClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={false}
      aria-labelledby="color-picker-dialog-title"
      PaperProps={{
        sx: {
          width: "auto",
          maxWidth: "none"
        }
      }}
    >
      <DialogTitle id="color-picker-dialog-title">
        {label}
        {subtitle ? (
          <div
            style={{
              fontSize: "0.9rem",
              fontStyle: "italic",
              fontWeight: "normal"
            }}
          >
            {subtitle}
          </div>
        ) : (
          <></>
        )}
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ width: 280, py: 1, px: 0 }}>
          {/* Custom Color Picker */}
          <Box sx={{ mb: 2 }}>
            <CustomColorPicker color={tempColor} onChange={handleColorChange} />
          </Box>

          {/* Input Controls */}
          <Box sx={{ mb: 2, width: 280 }}>
            {/* Hex Input */}
            <TextField
              label="Hex Value"
              value={hexInput}
              onChange={handleHexInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder="#000000"
              size="small"
              fullWidth
              error={!isValidHex(hexInput) && !!hexInput}
              helperText={
                !isValidHex(hexInput) && hexInput ? "Invalid hex color" : ""
              }
              InputProps={{
                style: { fontFamily: "monospace" }
              }}
            />
          </Box>

          {/* Preview */}
          <Box sx={{ mb: 1, width: 280 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  border: "2px solid #e0e0e0",
                  backgroundColor: tempColor,
                  flexShrink: 0
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Color
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}
                >
                  {tempColor}
                </Typography>
                {rgbValues && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    RGB({rgbValues.r}, {rgbValues.g}, {rgbValues.b})
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColorPickerDialog;
